<template>
  <data-table
    :key="tableKey"
    :title="title"
    :headers="headersLocal"
    :adjunctHeaders="adjunctHeaders"
    :itemTemplate="templateLocal"
    :adjunctItemTemplate="adjunctItemTemplate"
    :items="!items ? itemsLocal : items"
    :disabled="disabled || loading"
    :loading="loading"
    :allowDownload="allowDownload"
    :allowRefresh="allowRefresh"
    :allowAdd="allowAdd"
    :lastRowAdd="lastRowAdd"
    :allowEdit="allowEdit"
    :allowDelete="allowDelete"
    :allowSearch="allowSearch"
    :allowSort="allowSort"
    :allowDblClickEdit="allowEdit && allowDblClickEdit"
    :customButtons="customButtons"
    :revert="revert"
    @add-item="insertItem($event, true)"
    @update-item="updateItem($event, true)"
    @delete-item="deleteItem($event, true)"
    @refresh="selectItems()"
    @refresh-item="selectItem($event)"
    @download="download($event)"
    @copy="copy($event)"
    @custom-toolbar-button="$emit('custom-toolbar-button', $event)"
    @custom-item-action="$emit('custom-item-action', $event)"
    @reverted="revert = false"
    class="px-3"
  ></data-table>
</template>
<script>
import DataTable from './children/DataTable.vue';

const fetchV2 = require('../../assets/js/fetchV2');

export default {
  name: 'CrudElectron',

  components: {
    DataTable,
  },

  props: {
    title: {
      type: String,
      required: false,
    },

    crudRoute: {
      type: String,
      required: true,
    },

    joinItemsEndpoint: {
      type: Array,
      required: false,
      default: () => [],
    },

    headers: {
      type: Array,
      required: true,
    },

    addHeaders: {
      type: Array,
      required: false,
      default: () => [],
    },

    addHeadersEndpoint: {
      type: Array,
      required: false,
      default: () => [],
    },

    adjunctHeaders: {
      type: Array,
      required: false,
      default: () => [],
    },

    itemTemplate: {
      type: Object,
      required: true,
    },

    addTemplateItems: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    addTemplateItemsEndpoint: {
      type: Array,
      required: false,
      default: () => [],
    },

    adjunctItemTemplate: {
      type: Object,
      required: false,
      default: () => {},
    },

    items: {
      type: Array,
      required: false,
      default: null,
    },

    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },

    allowDownload: {
      type: Boolean,
      required: false,
      default: false,
    },

    allowRefresh: {
      type: Boolean,
      required: false,
      default: true,
    },

    allowAdd: {
      type: Boolean,
      required: false,
      default: true,
    },

    lastRowAdd: {
      type: Boolean,
      required: false,
      default: false,
    },

    allowEdit: {
      type: Boolean,
      required: false,
      default: true,
    },

    allowDelete: {
      type: Boolean,
      required: false,
      default: true,
    },

    allowSearch: {
      type: Boolean,
      required: false,
      default: true,
    },

    allowSort: {
      type: Boolean,
      required: false,
      default: true,
    },

    allowDblClickEdit: {
      type: Boolean,
      required: false,
      default: true,
    },

    customButtons: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  data: () => ({
    tableKey: 0,
    headersLocal: [],
    templateLocal: {},
    itemsLocal: [],
    loading: false,
    revert: false,
  }),

  watch: {
    itemTemplate: {
      handler() {
        this.getTemplateItems().then(() => {
          if (!this.items) {
            this.selectItems();
          }
        })
      },
      deep: true,
    },
  },

  computed: {
    primaryKeys() {
      return this.headers.filter(f => f.primaryKey).map(f => f.value);
    },
  },

  methods: {
    getItems() {
      return this.items ? this.items : this.itemsLocal;
    },

    setItems(items) {
      if (!this.items) {
        this.itemsLocal = items;
      }
      else {
        this.$emit('update-items', items);
      }
      this.tableKey++;
    },

    notEqualKeys(keys, a, b) {
      for (const key of keys) {
        if (a[key] !== b[key]) {
          return true;
        }
      }
      return false;
    },

    getHeaderItems() {
      return new Promise(async (resolve, reject) => {
        this.headersLocal = JSON.parse(JSON.stringify(this.headers));

        if (this.addHeaders && Array.isArray(this.addHeaders)) {
          this.headersLocal = this.headersLocal.concat(this.addHeaders);
        }

        if (this.addHeadersEndpoint && Array.isArray(this.addHeadersEndpoint) && this.addHeadersEndpoint.length > 0) {
          for (const endpoint of this.addHeadersEndpoint) {
            try {
              const additionalHeaders = await fetchV2(`GET ${endpoint}`, {}, { 'Cache-Control': 'no-cache' });
              this.headersLocal = this.headersLocal.concat(additionalHeaders);
            }
            catch (err) {
              this.$emit('error', 'Error fetching headers.');
              return reject();
            }
          }
        }

        return resolve();
      })
    },

    getTemplateItems() {
      return new Promise(async (resolve, reject) => {
        this.templateLocal = JSON.parse(JSON.stringify(this.itemTemplate));

        if (this.addTemplateItems && typeof this.addTemplateItems === 'object' && !Array.isArray(this.addTemplateItems)) {
          this.templateLocal = { ...this.templateLocal, ...this.addTemplateItems };
        }

        if (this.addTemplateItemsEndpoint && Array.isArray(this.addTemplateItemsEndpoint) && this.addTemplateItemsEndpoint.length > 0) {
          for (const endpoint of this.addTemplateItemsEndpoint) {
            try {
              const additionalItemTemplates = await fetchV2(`GET ${endpoint}`, {}, { 'Cache-Control': 'no-cache' });
              this.templateLocal = { ...this.templateLocal, ...additionalItemTemplates };
            }
            catch (err) {
              this.$emit('error', 'Error fetching items.');
              return reject();
            }
          }
        }

        for (const key of Object.keys(this.templateLocal)) {
          if (this.templateLocal[key].itemsEndpoint) {
            try {
              this.templateLocal[key].items = await fetchV2(`GET ${this.templateLocal[key].itemsEndpoint}`, {}, { 'Cache-Control': 'no-cache' });
              this.templateLocal[key].items.sort((a, b) => a.text.localeCompare(b.text))
            }
            catch (err) {
              this.$emit('error', 'Error fetching items.');
              return reject();
            }
          }
        }

        return resolve();
      })
    },

    selectItems(complete = true) {
      this.loading = true;

      return new Promise((resolve, reject) => {
        fetchV2(`GET ${this.crudRoute}`, {}, { 'Cache-Control': 'no-cache' }).then(async tableItems => {
          if (this.joinItemsEndpoint && Array.isArray(this.joinItemsEndpoint) && this.joinItemsEndpoint.length > 0) {
            for (const endpoint of this.joinItemsEndpoint) {
              try {
                const joinItems = await fetchV2(`GET ${endpoint.endpoint}`, {}, { 'Cache-Control': 'no-cache' });

                // Iterate through each new joined item
                for (const jItem of joinItems) {
                  // Find the corresponding item in the table to attach to
                  for (const tItem of tableItems) {
                    // Check if the main keys match the join keys
                    let match = true;
                    for (const i in endpoint.mainKeys) {
                      if (tItem[endpoint.mainKeys[i]] !== jItem[endpoint.joinKeys[i]]) {
                        match = false;
                        break;
                      }
                    }

                    // Attach the columns to the table item
                    if (match) {
                      for (const key of Object.keys(jItem)) {
                        if (!endpoint.joinKeys.includes(key)) {
                          tItem[key] = jItem[key];
                        }
                      }
                    }
                  }
                }

                this.setItems(tableItems);
                if (complete) {
                  this.$emit('success-banner', 'Items fetched successfully.');
                  this.loading = false;
                }
                return resolve();
              }
              catch (error) {
                console.error(err);
                if (complete) {
                  this.loading = false;
                  this.$emit('error', 'Error fetching items.');
                }
                return reject();
              }
            }
          }
          else {
            this.setItems(tableItems);
            if (complete) {
              this.$emit('success-banner', 'Items fetched successfully.');
              this.loading = false;
            }
            return resolve();
          }
        }).catch(err => {
          console.error(err);
          if (complete) {
            this.loading = false;
            this.$emit('error', 'Error fetching items.');
          }
          return reject();
        })
      })
    },

    selectItem(item, complete = true) {
      this.loading = true;

      return new Promise((resolve, reject) => {
        const newItem = {};
        for (const key of this.primaryKeys) {
          newItem[key] = item[key];
        }
        const query = Object.keys(newItem).map(key => `${key}=${newItem[key]}`).join('&');

        fetchV2(`GET ${this.crudRoute}?${query}`, {}, { 'Cache-Control': 'no-cache' }).then(data => {
          const idx = this.getItems().findIndex(f => this.primaryKeys.every(key => f[key] === data[0][key]));
          if (idx !== -1) {
            this.getItems()[idx] = data[0];
          }
          else {
            this.setItems([...this.getItems(), data[0]]);
          }

          if (complete) {
            this.$emit('success-banner', 'Item fetched successfully.');
            this.loading = false;
          }
          return resolve();
        }).catch(err => {
          console.error(err);
          if (complete) {
            this.loading = false;
            this.$emit('error', 'Error fetching item.');
          }
          return reject();
        })
      })
    },

    insertItem(item, complete = true) {
      this.loading = true;

      return new Promise((resolve, reject) => {

        fetchV2(`POST ${this.crudRoute}`, item).then(data => {
          this.setItems([...this.getItems(), data]);
          if (complete) {
            this.selectItems(false).then(() => {
              this.loading = false;
              this.$emit('success-banner', 'Item added successfully.');
            }).catch(err => {
              console.error(err);
              this.loading = false;
              this.$emit('error', 'Error updating item.');
            })
          }
          return resolve();
        }).catch(err => {
          console.error(err);
          this.revert = true;
          if (complete) {
            this.loading = false;
            this.$emit('error', 'Error adding item.');
          }
          return reject();
        })
      })
    },

    updateItem(items, complete = true) {
      this.loading = true;

      return new Promise((resolve, reject) => {
        const changedKeys = [];
        let pkChange = false;
        // Check if primary key has been modified and get changed fields
        for (const key of Object.keys(items.old)) {
          if (items.old[key] !== items.new[key]) {
            if (this.primaryKeys.includes(key)) {
              pkChange = true;
            }
            changedKeys.push(key);
          }
        }

        if (changedKeys.length === 0) {
          this.loading = false;
          return resolve();
        }

        // If primary key was modified, delete the old item and add the new item
        else if (pkChange) {
          this.deleteItem(items.old, false).then(() => {
            this.insertItem(items.new, false).then(() => {
              if (complete) {
                this.selectItems(false).then(() => {
                  this.loading = false;
                  this.$emit('success-banner', 'Item updated successfully.');
                }).catch(err => {
                  console.error(err);
                  this.loading = false;
                  this.$emit('error', 'Error updating item.');
                })
              }
              return resolve();
            }).catch(err => {
              console.error(err);
              this.setItems(this.getItems().filter(f => this.notEqualKeys(this.primaryKeys, f, items.old)));
              if (complete) {
                this.loading = false;
                this.$emit('error', 'Error updating item. The entry has been removed from the database.');
              }
              return reject();
            })
          }).catch(err => {
            console.error(err);
            this.revert = true;
            if (complete) {
              this.loading = false;
              this.$emit('error', 'Error updating item.');
            }
            return reject();
          })
        }
        // If primary key was not modified, update the item in-place
        else {
          fetchV2(`PUT ${this.crudRoute}`, items.new).then(data => {
            this.selectItem(items.new, false).then(() => {
              if (complete) {
                this.selectItems(false).then(() => {
                  this.loading = false;
                  this.$emit('success-banner', 'Item updated successfully.');
                }).catch(err => {
                  console.error(err);
                  this.loading = false;
                  this.$emit('error', 'Error updating item.');
                })
              }
              return resolve();
            }).catch(err => {
              console.error(err);
              if (complete) {
                this.loading = false;
                this.$emit('error', 'Error updating item.');
              }
              return reject();
            })
          }).catch(err => {
            console.error(err);
            this.revert = true;
            if (complete) {
              this.loading = false;
              this.$emit('error', 'Error updating items.');
            }
            return reject();
          })
        }
      })
    },

    deleteItem(item, complete = true) {
      this.loading = true;

      return new Promise((resolve, reject) => {
        fetchV2(`DELETE ${this.crudRoute}`, item).then(data => {
          this.setItems(this.getItems().filter(f => f.id !== item.id));
          if (complete) {
            this.selectItems(false).then(() => {
              this.loading = false;
              this.$emit('success-banner', 'Item deleted successfully.');
            }).catch(err => {
              console.error(err);
              this.loading = false;
              this.$emit('error', 'Error updating item.');
            })
          }
          return resolve();
        }).catch(err => {
          console.error(err);
          this.revert = true;
          if (complete) {
            this.loading = false;
            this.$emit('error', 'Error deleting items.');
          }
          return reject();
        })
      })
    },
  },

  destroyed() {

  },

  created() {
    this.loading = true;
    this.getHeaderItems().then(() => {
      this.getTemplateItems().then(() => {
        this.loading = false;
        if (!this.items) {
          this.selectItems();
        }
      }).catch(err => {
        this.loading = false;
        console.error(err);
        this.$emit('error', 'Error fetching items.');
      })
    }).catch(err => {
      this.loading = false;
      console.error(err);
      this.$emit('error', 'Error fetching headers.');
    })
  },

  mounted() {

  }
}
</script>
